import { useState } from 'react'
import useFetchApiData from './GetStudentList'
import useSignalRChat from 'src/Hooks/CounselorHooks/SingleR/useSignalRChat'
import { message } from 'antd'

// Map a raw chat message (from Get-Messages/List) into the shape the thread renders.
const mapChatMessage = (msg, members = []) => {
  const sender = members.find((member) => member.userId === msg.senderId)
  return {
    text: msg.content,
    messageId: msg.messageId ?? msg.id,
    attachmentFileAssetId: msg.attachmentFileAssetId,
    attachmentName: msg.attachmentName,
    attachmentThumbnailUrl: msg.attachmentThumbnailUrl,
    messageType: msg.messageType,
    senderId: msg.senderId,
    senderName: sender ? sender.name : 'Unknown',
    senderProfile: sender ? sender.profileUrl : '',
    isSentByUser: msg.isSentByUser,
    sentAt: msg.sentAt,
  }
}

const useCounselorChat = () => {
  const userId = localStorage.getItem('userId')
  const token = localStorage.getItem('token')
  const role = localStorage.getItem('role')
  const { students, groupChat, tutors, counselors, groups } = useFetchApiData()
  const [activeChats, setActiveChats] = useState([])
  const [selectedChatId, setSelectedChatId] = useState(null)
  const [search, setSearch] = useState('')
  const [modelSearch, setModelSearch] = useState('')
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isGroupModalVisible, setIsGroupModalVisible] = useState(false)
  const [mentorModelVisible, setMentorModelVisible] = useState(false)
  const [isTutorModalVisible, setIsTutorModalVisible] = useState(false)
  const [hoveredId, setHoveredId] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState([])
  const [chatMessages, setChatMessages] = useState([])
  const [chatDetails, setChatDetails] = useState(null)
  const [conversations, setConversations] = useState([])
  const [loader, setLoader] = useState(true)
  const [msgLoader, setMsgLoader] = useState(true)
  const [hubConnection, setHubConnection] = useState(null)
  const [msgPage, setMsgPage] = useState(1)
  const [msgTotalPages, setMsgTotalPages] = useState(1)
  const [loadingOlder, setLoadingOlder] = useState(false)

  //Single R
  useSignalRChat(setChatMessages, setConversations, selectedChatId, setLoader, setHubConnection)

  const startChat = async (student) => {
    try {
      const payload = { receiverId: student.id }

      const response = await fetch(`${process.env.REACT_APP_BASE_URL_API}/Chats/private/Create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error Response:', errorText)
        throw new Error(`Failed to start chat: ${errorText}`)
      }

      const data = await response.json()

      if (!(data.conversationId ?? data.id)) {
        throw new Error('Missing conversationId in response')
      }

      const newChat = {
        id: data.conversationId ?? data.id,
        name: student.name,
        type: 'single',
        messages: [],
      }

      setActiveChats((prevChats) => {
        if (!prevChats.some((chat) => chat.id === newChat.id)) {
          return [...prevChats, newChat]
        }
        return prevChats
      })

      setSelectedChatId(newChat.id)
      setIsModalVisible(false)
      setIsTutorModalVisible(false)
    } catch (error) {
      console.error('Error starting chat:', error)
    }
  }

  const startGroupChat = async (student) => {
    try {
      const payload =
        role === 'Parent'
          ? { counselorId: student.counselorId, studentId: student.id }
          : { studentId: student.id }
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL_API}/Chats/group/student-parent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      )
      if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error Response:', errorText)
        throw new Error(`Failed to start chat: ${errorText}`)
      }

      const data = await response.json()

      const newChat = {
        id: data.conversationId ?? data.id,
        name: student.name,
        type: 'group',
        messages: [],
      }

      setActiveChats((prevChats) => {
        if (!prevChats.some((chat) => chat.id === newChat.id)) {
          return [...prevChats, newChat]
        }
        return prevChats
      })

      setSelectedChatId(newChat.id)
      setIsGroupModalVisible(false)
    } catch (error) {
      console.error('Error starting chat:', error)
    }
  }

  const startMentorGroupChat = async (mentorGroup) => {
    try {
      const payload = {
        groupId: mentorGroup.groupId,
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL_API}/Chats/mentor/group/Create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      )

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error Response:', errorText)
        throw new Error(`Failed to start chat: ${errorText}`)
      }

      const data = await response.json()

      const newChat = {
        id: data.conversationId ?? data.id,
        name: mentorGroup.name,
        type: 'group',
        messages: [],
      }

      setActiveChats((prevChats) => {
        if (!prevChats.some((chat) => chat.id === newChat.id)) {
          return [...prevChats, newChat]
        }
        return prevChats
      })

      setSelectedChatId(newChat.id)

      setMentorModelVisible(false)
    } catch (error) {
      console.error('Error starting chat:', error)
    }
  }

  const sendMessage = async (chatId, message, file = null) => {
    try {
      setLoading(true)

      const formData = new FormData()
      formData.append('conversationId', chatId)
      formData.append('content', message || '') // Ensure empty message is handled
      formData.append('messageType', file ? 2 : 1)

      if (file) {
        formData.append('attachment', file, file.name)
      }

      // // Debugging: Check what data is being sent
      // for (let [key, value] of formData.entries()) {
      //   console.log(`${key}:`, value)
      // }

      const response = await fetch(`${process.env.REACT_APP_BASE_URL_API}/Chats/Send-messages`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Server Response Error:', errorData)
        throw new Error(errorData.message || 'Failed to send message')
      }

      // The send response contains the created message (Get-Messages currently omits
      // the sender's own messages on the backend), so render it from here.
      const sent = await response.json().catch(() => null)

      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          (conv.id ?? conv.conversationId) === chatId
            ? { ...conv, chatSubTitle: message || (file && file.name) || '' }
            : conv,
        ),
      )
      // Show the sent message in the main thread right away and keep it there.
      // It is tagged __local so the refetch merge does not wipe it while the backend
      // Get-Messages returns an empty list; once that endpoint returns the message,
      // the merge dedupes it by messageId.
      setChatMessages((prev) => [
        ...prev,
        {
          text: (sent && sent.content) || message || '',
          isSentByUser: true,
          senderId: Number((sent && sent.senderId) ?? userId),
          messageId: sent && (sent.messageId ?? sent.id),
          messageType: (sent && sent.messageType) ?? (file ? 2 : 1),
          attachmentName: (sent && sent.attachmentName) ?? (file ? file.name : undefined),
          attachmentThumbnailUrl: sent && sent.attachmentThumbnailUrl,
          sentAt: (sent && sent.sentAt) || new Date().toISOString(),
          __local: true,
          __convId: chatId,
        },
      ])
      fetchChatDetails(chatId)
    } catch (error) {
      console.log('error', error)
      console.error('Error sending message:', error)
    } finally {
      setLoading(false)
    }
  }

  const allowedExtensions = [
    'pdf',
    'PDF',
    'doc',
    'DOC',
    'docx',
    'DOCX',
    'xls',
    'XLS',
    'xlsx',
    'XLSX',
    'CSV',
    'csv',
  ]
  const allowedMimeTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ]

  const maxFileSize = 10 * 1024 * 1024 // 10MB

  const handleFileUpload = (file) => {
    if (!file || !file.name) {
      message.error('Invalid file. Please upload a valid file.')
      return false
    }

    // Extract file extension safely
    const fileExtension = file.name.split('.').pop().toLowerCase()

    // Check both extension and MIME type
    if (!allowedExtensions.includes(fileExtension) && !allowedMimeTypes.includes(file.type)) {
      message.error('Invalid file type. Allowed types: PDF, DOC, DOCX, XLS, XLSX, CSV')
      return false
    }

    if (file.size > maxFileSize) {
      message.error('File size exceeds 10MB limit')
      return false
    }

    return true
  }

  const fetchChatDetails = async (conversationId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL_API}/Chats/Get-Messages?conversationId=${conversationId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (!response.ok) {
        throw new Error('Failed to fetch chat details')
      }

      const data = await response.json()
      const canUploadAttachment = data.canUploadAttachment ?? false
      const members = data.members ?? []
      // Get-Messages returns only conversation metadata (title, members, attachment
      // permission). The actual messages come from the paginated List endpoint, newest
      // first, so we reverse them to show oldest -> newest in the thread.
      let rawMessages = []
      try {
        const listRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_API}/Chats/Get-Messages/List?conversationId=${conversationId}&pageNumber=1&pageSize=30&orderByLatest=true`,
          { method: 'GET', headers: { Authorization: `Bearer ${token}` } },
        )
        if (listRes.ok) {
          const listData = await listRes.json()
          rawMessages = Array.isArray(listData?.items) ? [...listData.items].reverse() : []
          setMsgPage(1)
          setMsgTotalPages(listData?.totalPages || 1)
        }
      } catch (err) {
        rawMessages = []
      }
      const formattedMessages = rawMessages.map((msg) => mapChatMessage(msg, members))

      setChatDetails({
        id: data.conversationId ?? data.id,
        name: data.chatTitle,
        type: data.chatType === 2 ? 'group' : 'single',
        members: data.members || [],
        canUploadAttachment,
      })
      // Merge: keep locally-sent messages for this conversation that the server has
      // not returned yet (backend Get-Messages omits the sender's own messages).
      setChatMessages((prev) => {
        const serverIds = new Set(
          formattedMessages.map((m) => m.messageId).filter((x) => x !== undefined && x !== null),
        )
        const keptLocal = prev.filter(
          (m) =>
            m.__local &&
            Number(m.__convId) === Number(conversationId) &&
            !serverIds.has(m.messageId),
        )
        return [...formattedMessages, ...keptLocal]
      })
      if (hubConnection) {
        await hubConnection.invoke('SetActiveConversation', conversationId)
        // console.log('✅ Active conversation set:', conversationId)
      }
    } catch (error) {
      console.error('Error fetching chat details:', error)
    } finally {
      setMsgLoader(false)
    }
  }

  // Prepend the next older page of messages (Get-Messages/List is newest-first, so reverse
  // each page and prepend). The caller pins the scroll so the view does not jump to the bottom.
  const loadOlderMessages = async () => {
    if (loadingOlder || msgPage >= msgTotalPages || !selectedChatId) return
    setLoadingOlder(true)
    try {
      const next = msgPage + 1
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL_API}/Chats/Get-Messages/List?conversationId=${selectedChatId}&pageNumber=${next}&pageSize=30&orderByLatest=true`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } },
      )
      if (res.ok) {
        const d = await res.json()
        const older = Array.isArray(d?.items) ? [...d.items].reverse() : []
        const members = chatDetails?.members || []
        const mapped = older.map((msg) => mapChatMessage(msg, members))
        setChatMessages((prev) => [...mapped, ...prev])
        setMsgPage(next)
      }
    } catch (err) {
      // older messages simply will not load; the latest page stays visible
    } finally {
      setLoadingOlder(false)
    }
  }

  return {
    loadOlderMessages,
    hasMoreMessages: msgPage < msgTotalPages,
    loadingOlder,
    handleFileUpload,
    hubConnection,
    userId,
    token,
    students,
    groupChat,
    groups,
    tutors,
    counselors,
    activeChats,
    isModalVisible,
    loading,
    mentorModelVisible,
    isGroupModalVisible,
    isTutorModalVisible,
    setSelectedGroup,
    chatMessages,
    chatDetails,
    startChat,
    startGroupChat,
    sendMessage,
    hoveredId,
    setHoveredId,
    search,
    setSearch,
    modelSearch,
    setModelSearch,
    setIsModalVisible,
    setMentorModelVisible,
    setIsGroupModalVisible,
    setIsTutorModalVisible,
    setSelectedChatId,
    selectedChatId,
    fetchChatDetails,
    setChatDetails,
    setChatMessages,
    selectedGroup,
    loader,
    conversations,
    msgLoader,
    role,
    startMentorGroupChat,
  }
}

export default useCounselorChat
